// src/pages/certificate-public/index.jsx
//
// Página PÚBLICA (sin login) que abre el QR del sticker: /c/6499001-a1b2c3d4e5
// La escanea el operario con el celular parado al lado del equipo, así que es
// mobile-first y muestra de un vistazo si la calibración está vigente.
//
// Los datos vienen de get_certificate_public(numero, token): sólo lo que
// corresponde ver. El token evita que se puedan enumerar los certificados.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';

function fmtDate(v) {
  if (!v) return '—';
  const [y, m, d] = String(v).slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : '—';
}

// vigente / por vencer (<=30 días) / vencido
function vigencia(validUntil) {
  if (!validUntil) return { key: 'sin', label: 'Sin vencimiento registrado', cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30' };
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const fin = new Date(`${String(validUntil).slice(0, 10)}T12:00:00`);
  const dias = Math.round((fin - hoy) / 86400000);
  if (dias < 0) return { key: 'vencido', label: 'VENCIDO', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/40' };
  if (dias <= 30) return { key: 'por-vencer', label: `Vence en ${dias} día${dias === 1 ? '' : 's'}`, cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' };
  return { key: 'vigente', label: 'VIGENTE', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' };
}

function Row({ k, v }) {
  if (!v) return null;
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-white/5 last:border-0">
      <span className="text-sm text-slate-400 shrink-0">{k}</span>
      <span className="text-sm text-white font-medium text-right break-words">{v}</span>
    </div>
  );
}

export default function CertificatePublicPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, cert: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      // slug = "6499001-a1b2c3d4e5": el número es todo lo previo al primer guion.
      const s = String(slug || '');
      const i = s.indexOf('-');
      const number = i > 0 ? s.slice(0, i) : s;
      const token = i > 0 ? s.slice(i + 1) : '';
      if (!number || !token) { if (alive) setState({ loading: false, cert: null }); return; }
      try {
        const { data, error } = await supabase.rpc('get_certificate_public', {
          p_number: number,
          p_token: token,
        });
        if (error) throw error;
        if (alive) setState({ loading: false, cert: data || null });
      } catch (e) {
        console.error('[CertificadoPúblico]', e);
        if (alive) setState({ loading: false, cert: null });
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const { loading, cert } = state;

  return (
    <div className="min-h-screen bg-[#0b0f14] text-white px-4 py-8">
      <div className="mx-auto w-full max-w-md">
        <div className="text-center mb-6">
          <div className="text-2xl font-bold tracking-tight">Calins</div>
          <div className="text-xs text-slate-400 mt-0.5">Laboratorio de calibración</div>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center text-slate-400">
            Buscando el certificado…
          </div>
        ) : !cert ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 space-y-2 text-center">
            <div className="text-4xl">🔍</div>
            <h1 className="text-lg font-semibold">Certificado no encontrado</h1>
            <p className="text-sm text-slate-400">
              El enlace no es válido o el certificado ya no está disponible. Verificá el código
              del sticker o escribinos a{' '}
              <a className="text-sky-400 underline" href="mailto:laboratorio@calins.com.ar">laboratorio@calins.com.ar</a>.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {(() => {
              const v = vigencia(cert.valid_until);
              const inst = cert.instrument || {};
              const nombre = [inst.brand, inst.model].filter(Boolean).join(' ') || inst.type || 'Instrumento';
              return (
                <>
                  <div className={`rounded-2xl border px-4 py-4 text-center ${v.cls}`}>
                    <div className="text-xs uppercase tracking-wide opacity-80">Estado de calibración</div>
                    <div className="text-2xl font-extrabold mt-0.5">{v.label}</div>
                    {cert.valid_until && (
                      <div className="text-xs mt-1 opacity-80">Válido hasta {fmtDate(cert.valid_until)}</div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="text-center pb-3 border-b border-white/5">
                      <div className="text-xs text-slate-400">Certificado N°</div>
                      <div className="text-2xl font-bold tracking-tight">{cert.certificate_number}</div>
                    </div>
                    <div className="pt-1">
                      <Row k="Instrumento" v={nombre} />
                      <Row k="ID" v={inst.lab_id != null ? `#${inst.lab_id}` : null} />
                      <Row k="TAG" v={inst.tag} />
                      <Row k="N° de serie" v={inst.serial} />
                      <Row k="Cliente" v={cert.client_name} />
                      <Row k="Fecha de calibración" v={fmtDate(cert.calibration_date)} />
                      <Row k="Próxima calibración" v={fmtDate(cert.valid_until)} />
                    </div>
                  </div>

                  {cert.pdf_url && (
                    <a
                      href={cert.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center rounded-xl bg-sky-600 hover:bg-sky-500 transition px-4 py-4 font-semibold"
                    >
                      📄 Descargar certificado (PDF)
                    </a>
                  )}

                  <p className="text-[11px] text-slate-500 text-center leading-relaxed pt-1">
                    Certificado emitido por Calins Laboratorio. Ante cualquier duda sobre su
                    autenticidad, escribinos a laboratorio@calins.com.ar
                  </p>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
